import { normalizeError } from 'core/common/errors';
import { Logger } from 'core/common/services/logger/interfaces';
import { featureFlagsInitialState } from 'core/feature-flags/store/state';
import { offersInitialState } from 'core/offers/store/state';
import { LocalStorageState, SessionStorageState, StoreState } from '../entities';
import { RootInitialState } from '../rootReducer';
import { StorePersistenceInterface } from './interfaces';

export class StorePersistenceClientService implements StorePersistenceInterface {
  private keyPrefix = 'nebula';
  private logger: Logger;

  constructor(logger: Logger) {
    this.logger = logger;
  }

  load(): Partial<StoreState> {
    try {
      return {
        ...this.loadStateFromLocalStorage(),
        ...this.loadStateFromSessionStorage(),
      };
    } catch (error) {
      this.logger.error(normalizeError(error));
      throw error;
    }
  }

  save(store: RootInitialState): void {
    try {
      this.saveToLocalStorage(store);
      this.saveToSessionStorage(store);
    } catch (error) {
      this.logger.error(normalizeError(error));
      throw error;
    }
  }

  private loadStateFromLocalStorage(): Partial<LocalStorageState> {
    const serializedLocalStorage = localStorage.getItem(this.keyPrefix);

    if (serializedLocalStorage === null) {
      return {};
    }
    const { userProfile, offers, humanDesign, featureFlags, userLocation }: RootInitialState =
      JSON.parse(serializedLocalStorage);

    return {
      userProfile,
      offers: {
        ...offersInitialState,
        ...offers,
      },
      humanDesign,
      featureFlags: {
        ...featureFlagsInitialState,
        ...featureFlags,
      },
      userLocation,
    };
  }

  private loadStateFromSessionStorage(): SessionStorageState {
    const serializedSessionStorage = sessionStorage.getItem(this.keyPrefix);

    if (!serializedSessionStorage) {
      return {};
    }
    const {
      auth,
      payments,
      userResponses,
      agreement,
      userInteraction,
      natalChart,
      content,
      userSubscriptions,
      scan,
    } = JSON.parse(serializedSessionStorage);

    return {
      auth,
      payments,
      userResponses,
      userInteraction,
      agreement,
      natalChart,
      content,
      userSubscriptions,
      scan: {
        data: scan,
      },
    };
  }

  private saveToLocalStorage(store: RootInitialState): void {
    const { loadingStatus: _, config: __, ...featureFlags } = store.featureFlags;

    const serializedLocalStorage = JSON.stringify({
      userProfile: store.userProfile,
      offers: store.offers,
      humanDesign: store.humanDesign,
      featureFlags,
    });

    localStorage.setItem(this.keyPrefix, serializedLocalStorage);
  }

  private saveToSessionStorage(store: RootInitialState): void {
    const serializedSessionStorage = JSON.stringify({
      auth: store.auth,
      payments: store.payments,
      userResponses: store.userResponses,
      userInteraction: store.userInteraction,
      agreement: store.agreement,
      natalChart: store.natalChart,
      content: store.content,
      userSubscriptions: store.userSubscriptions,
      scan: store.scan.data,
    });

    sessionStorage.setItem(this.keyPrefix, serializedSessionStorage);
  }
}
